import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import { ActionButton, DialogFooter, IDialogProps, SearchBox } from '@fluentui/react'
import LegacyBaseDialog from '../LegacyBaseDialog'
import useEntity from '../../../hooks/useEntity'
import KvkSearchResultList, { KvkSearchResult } from '../../List/KvkSearchResult/KvkSearchResultList'
import Loading from '../../Loading'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { LegalStructure } from '../../../config/i18nConfig'
import useLegalStructures from '../../../hooks/useLegalStructures'
import useHost from '../../../hooks/useHost'
import { Destination } from '../../../context/AxiosContext'
import { LegalPersonMutationDto } from '../../Form/Party/LegalPersonForm'

type KvkSearchPageResult = {
    kvkNummer: string
    naam: string
    adres: KvkSearchAddress
    type: 'hoofdvestiging' | 'rechtspersoon' | string
}

type KvkSearchAddress = {
    binnenlandsAdres: KvkSearchDutchAddress
    buitenlandsAdres: KvkSearchForeignAddress
}

type KvkSearchBaseAddress = {
    type: 'bezoekadres' | 'correspondentieadres'
}

type KvkSearchDutchAddress = KvkSearchBaseAddress & {
    straatnaam: string
    huisnummer: number
    huisletter: string
    postbusnummer: number
    postcode: string
    plaats: string
}

type KvkSearchForeignAddress = KvkSearchBaseAddress & {
    straatHuisnummer: string
    postcodeWoonplaats: string
    land: string
}

export type KvkSearchPage = {
    pagina: number
    aantal: number
    totaal: number
    resultaten: KvkSearchPageResult[]
}

type KvkLegalStructures =
    | 'Besloten Vennootschap'
    | 'Naamloze Vennootschap'
    | 'Eenmanszaak'
    | 'Stichting'
    | 'Vennootschap Onder Firma'
    | 'Maatschap'
    | 'Vereniging'
    | 'Commanditaire Vennootschap'
    | 'Coöperatie'
    | 'Publiekrechtelijke Rechtspersoon'

type KvkProfileAddress = {
    type: 'bezoekadres' | 'correspondentieadres'
    postcode: string
    plaats: string
    land: string
    postbusnummer?: string
    straatnaam?: string
    huisnummer?: string
    huisnummerToevoeging: string
}

type KvkProfilePage = {
    kvkNummer: string
    naam: string
    _embedded: {
        hoofdvestiging?: {
            adressen: KvkProfileAddress[]
        }
        eigenaar: {
            rechtsvorm: KvkLegalStructures
            adressen?: KvkProfileAddress[]
        }
    }
}
export type KvkError = { code: string; omschrijving: string }

export type KvkErrorResponse = {
    fout: KvkError[]
}

export const isKvkError = (error: AxiosError): error is AxiosError<KvkErrorResponse> => !!(error.response?.data as KvkErrorResponse)?.fout

export type KvkSearchDialogProps = IDialogProps & {
    onSelectCompany: (company: Partial<LegalPersonMutationDto>) => void
}

const mapSearchResult = (page: KvkSearchPage): KvkSearchResult[] => {
    return page.resultaten
        .filter(a => a.adres)
        .sort((a: KvkSearchPageResult, b: KvkSearchPageResult) => a.kvkNummer.localeCompare(b.kvkNummer))
        .map<KvkSearchResult>(entry => ({
            number: entry.kvkNummer,
            name: entry.naam,
            street: entry.adres.binnenlandsAdres
                ? [entry.adres.binnenlandsAdres.straatnaam, entry.adres.binnenlandsAdres.huisletter, entry.adres.binnenlandsAdres.postbusnummer]
                      .filter(field => field)
                      .join(' ')
                : entry.adres.buitenlandsAdres.straatHuisnummer,
            locality: entry.adres.binnenlandsAdres
                ? entry.adres.binnenlandsAdres.plaats
                : `${entry.adres.buitenlandsAdres.postcodeWoonplaats} ${entry.adres.buitenlandsAdres.land}`,
        }))
        .reduce(
            (acc: KvkSearchResult[], current: KvkSearchResult) => (acc.find(entry => entry.number === current.number) ? acc : [...acc, current]),
            [],
        )
}

const mapProfileResult = (getLegalStructureByName: (name: string) => LegalStructure, page: KvkProfilePage): Partial<LegalPersonMutationDto> => {
    const addresses = [...(page._embedded.hoofdvestiging?.adressen || []), ...(page._embedded.eigenaar?.adressen || [])]
    const address = addresses.find(address => address.type === 'bezoekadres')
    const poBoxAddress = addresses.find(address => address.type === 'correspondentieadres')

    return {
        cocNumber: page.kvkNummer,
        name: page.naam,
        legalStructure: getLegalStructureByName(page._embedded.eigenaar.rechtsvorm)?.alpha,
        addressStreet: address?.postbusnummer ? 'Postbus' : address?.straatnaam,
        addressNumber: address?.postbusnummer ?? `${address?.huisnummer ?? ''}${address?.huisnummerToevoeging ?? ''}`,
        addressPostalCode: address?.postcode,
        addressLocality: address?.plaats,
        addressCountry: 'NL',
        poBoxAddressStreet: poBoxAddress?.postbusnummer ? 'Postbus' : poBoxAddress?.straatnaam,
        poBoxAddressNumber: poBoxAddress?.postbusnummer ?? `${poBoxAddress?.huisnummer ?? ''}${poBoxAddress?.huisnummerToevoeging ?? ''}`,
        poBoxAddressPostalCode: poBoxAddress?.postcode,
        poBoxAddressLocality: poBoxAddress?.plaats,
        poBoxAddressCountry: poBoxAddress ? 'NL' : undefined,
    }
}

export default function KvkSearchDialog(props: KvkSearchDialogProps): ReactElement {
    const host = useHost()
    const { t: translate } = useTranslation()
    const { getLegalStructureByName } = useLegalStructures()

    const [query, setQuery] = useState<string>(null)
    const [number, setNumber] = useState<string>(null)
    const [items] = useEntity<KvkSearchResult[], KvkSearchPage, KvkErrorResponse>(
        query => ({
            url: `/coc/kvk/search/${host.RowKey}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
            params: { query },
        }),
        query !== '' ? query : null,
        result => mapSearchResult(result.data),
    )
    const [selectedItem] = useEntity<Partial<LegalPersonMutationDto>, KvkProfilePage, KvkErrorResponse>(
        number => ({
            url: `/coc/kvk/profile/${host.RowKey}/${number}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
        }),
        number,
        result => mapProfileResult(getLegalStructureByName, result.data),
    )
    const onDismiss = () => {
        setQuery(null)
        setNumber(null)
        props.onDismiss?.()
    }

    useEffect(() => {
        if (selectedItem.loaded) {
            props.onSelectCompany(selectedItem.data)
            onDismiss()
        }
    }, [selectedItem])

    return (
        <LegacyBaseDialog {...props} dialogContentProps={{ title: translate('party.edit.coc.search.title') }} maxWidth={960} onDismiss={onDismiss}>
            {selectedItem.loading ? (
                <Loading />
            ) : (
                <>
                    <SearchBox underlined onSearch={setQuery} onClear={() => setQuery(null)} />
                    <KvkSearchResultList items={items} setNumber={setNumber} />
                </>
            )}
            <DialogFooter>
                <ActionButton text={translate('action.cancel')} onClick={onDismiss} />
            </DialogFooter>
        </LegacyBaseDialog>
    )
}
